import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Container,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useRecipes } from '../context/RecipeContext';
import {
  BookContainer,
  BookPage,
  BookNavigation,
  RecipeSidebar,
  RecipeStepPage,
} from '../components';
import AIChatDialog from '../components/AIChatDialog';
import CookingPromptOverlay from '../components/CookingPromptOverlay';
import audioService from '../services/audioService';
import { Recipe, RecipeStep } from '../models/Recipe';
import { STORAGE_KEYS } from '../utils/constants';

const RecipeStepScreen: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { id: recipeId, stepIndex: stepIndexParam } = useParams<{ id: string; stepIndex: string }>();
  const stepIndex = parseInt(stepIndexParam || '0', 10);
  
  const { 
    selectedRecipe, 
    loading, 
    error, 
    getRecipeById,
    generateStepImage,
    generateStepAudio,
    updateRecipe,
  } = useRecipes();
  
  const [recipe, setRecipe] = useState<Recipe | null>(selectedRecipe);
  const [currentStep, setCurrentStep] = useState<RecipeStep | null>(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState<boolean>(false);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState<boolean>(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState<boolean>(false);
  const [aiChatOpen, setAiChatOpen] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [completedSteps] = useState<Set<number>>(new Set());
  const [currentScale] = useState<number>(1);
  const [pendingTimer, setPendingTimer] = useState<{ duration: number; label: string } | null>(null);
  // const [timeOnStep, setTimeOnStep] = useState<number>(0); // Unused
  const [showPrompt, setShowPrompt] = useState<boolean>(false);
  // const [promptDismissed, setPromptDismissed] = useState<boolean>(false); // Unused
  const [initialChatMessage, setInitialChatMessage] = useState<string>('');

  useEffect(() => {
    const fetchRecipe = async () => {
      if (!recipe && recipeId) {
        const fetchedRecipe = await getRecipeById(recipeId);
        setRecipe(fetchedRecipe);
      }
    };

    fetchRecipe();
  }, [recipeId, recipe, getRecipeById]);

  useEffect(() => {
    if (recipe && recipe.steps && recipe.steps.length > 0) {
      if (stepIndex >= 0 && stepIndex < recipe.steps.length) {
        setCurrentStep(recipe.steps[stepIndex]);
      } else {
        navigate(`/recipes/${recipeId}/steps/0`);
      }
    }
  }, [recipe, stepIndex, recipeId, navigate]);

  // Reset timer and prompt state when step changes
  useEffect(() => {
    // setTimeOnStep(0);
    setShowPrompt(false);
    // setPromptDismissed(false);

    // const interval = setInterval(() => {
    //   setTimeOnStep(prev => prev + 1);
    // }, 1000);

    // return () => clearInterval(interval);
  }, [stepIndex]);

  // Show proactive prompt after spending time on a step
  // DISABLED for "Interactive Book" feel - preventing unwanted interruptions
  /*
  useEffect(() => {
    // Show prompt after 45 seconds on a step, if not dismissed and chat not open
    if (timeOnStep >= 45 && !promptDismissed && !aiChatOpen && !showPrompt) {
      setShowPrompt(true);
    }
  }, [timeOnStep, promptDismissed, aiChatOpen, showPrompt]);
  */

  const handleStepClick = (targetStepIndex: number) => {
    navigate(`/recipes/${recipeId}/steps/${targetStepIndex}`);
  };

  const handleGenerateImage = useCallback(async () => {
    if (recipe && currentStep) {
      try {
        setIsGeneratingImage(true);
        const updatedStep = await generateStepImage(currentStep, recipe.title);

        // Update the current step with the generated image
        if (recipe.steps) {
          const updatedSteps = [...recipe.steps];
          updatedSteps[stepIndex] = updatedStep;
          setRecipe({
            ...recipe,
            steps: updatedSteps,
          });
          setCurrentStep(updatedStep);
        }
      } catch (error) {
        console.error('Error generating image:', error);
        setSnackbarMessage(`Failed to generate image: ${error instanceof Error ? error.message : 'Unknown error'}`);
        setSnackbarOpen(true);
      } finally {
        setIsGeneratingImage(false);
      }
    }
  }, [recipe, currentStep, generateStepImage, stepIndex, setRecipe]);

  const handleGenerateAudio = useCallback(async () => {
    if (currentStep) {
      try {
        setIsGeneratingAudio(true);
        const updatedStep = await generateStepAudio(currentStep);

        // Update the current step with the generated audio
        if (recipe && recipe.steps) {
          const updatedSteps = [...recipe.steps];
          updatedSteps[stepIndex] = updatedStep;
          setRecipe({
            ...recipe,
            steps: updatedSteps,
          });
          setCurrentStep(updatedStep);
        }
      } catch (error) {
        console.error('Error generating audio:', error);
        setSnackbarMessage(`Failed to generate audio: ${error instanceof Error ? error.message : 'Unknown error'}`);
        setSnackbarOpen(true);
      } finally {
        setIsGeneratingAudio(false);
      }
    }
  }, [currentStep, generateStepAudio, recipe, stepIndex, setRecipe]);

  // Auto generation when step changes
  useEffect(() => {
    if (currentStep && recipe && !isGeneratingImage && !isGeneratingAudio) {
      const autoImage = localStorage.getItem(STORAGE_KEYS.AUTO_IMAGE) === 'true';
      const autoSpeech = localStorage.getItem(STORAGE_KEYS.AUTO_SPEECH) === 'true';
      const imageGeneration = localStorage.getItem(STORAGE_KEYS.IMAGE_GENERATION) !== 'false';
      const audioGeneration = localStorage.getItem(STORAGE_KEYS.AUDIO_GENERATION) !== 'false';

      // Small delay to ensure the step is fully loaded before checking for auto-generation
      const timer = setTimeout(() => {
        // Auto-generate image if enabled and step doesn't have an image
        if (autoImage && imageGeneration && !currentStep.image && !isGeneratingImage) {
          handleGenerateImage();
        }

        // Auto-generate audio if enabled and step doesn't have audio  
        if (autoSpeech && audioGeneration && !currentStep.audio && !isGeneratingAudio) {
          handleGenerateAudio();
        }
      }, 500);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [currentStep?.content, currentStep?.image, currentStep?.audio, recipe?.id, handleGenerateImage, handleGenerateAudio, isGeneratingImage, isGeneratingAudio]);

  const handlePlayAudio = async () => {
    if (currentStep && currentStep.audio) {
      if (isPlayingAudio) {
        console.log('Stopping audio playback');
        await audioService.stopAudio();
        setIsPlayingAudio(false);
      } else {
        setIsPlayingAudio(true);
        try {
          await audioService.playAudio(currentStep.audio);
        } catch (error) {
          console.error('Error playing audio:', error);
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          setSnackbarMessage(`Failed to play audio: ${errorMessage}`);
          setSnackbarOpen(true);
        } finally {
          setIsPlayingAudio(false);
        }
      }
    } else if (currentStep && !isGeneratingAudio) {
      handleGenerateAudio();
    }
  };

  // (Old question dialog and response dialog code removed)

  const handleTimerComplete = (_timerId: string, timerName: string) => {
    setSnackbarMessage(`Timer "${timerName}" has finished!`);
    setSnackbarOpen(true);
  };

  const handleStartTimer = (duration: number, label: string) => {
    setPendingTimer({ duration, label });
    // Clear after a short delay to allow the timer to be created
    setTimeout(() => {
      setPendingTimer(null);
    }, 1000);
  };

  const handleRecipeUpdate = (updatedRecipe: Recipe, changes: string[]) => {
    setRecipe(updatedRecipe);
    
    // Update current step if within bounds
    if (updatedRecipe.steps && stepIndex < updatedRecipe.steps.length) {
      setCurrentStep(updatedRecipe.steps[stepIndex]);
    }

    // Show success message with changes
    const changesText = changes.length > 0 
      ? `Recipe updated: ${changes.join(', ')}`
      : 'Recipe updated with ingredient substitution';
    
    setSnackbarMessage(changesText);
    setSnackbarOpen(true);
  };

  const handleOpenQuestionDialog = () => {
    setAiChatOpen(true);
    setShowPrompt(false);
  };

  const handlePromptClick = (promptText: string) => {
    setShowPrompt(false);
    setInitialChatMessage(promptText);
    setAiChatOpen(true);
  };

  const handlePromptDismiss = () => {
    setShowPrompt(false);
    // setPromptDismissed(true);
  };

  const handleChatClose = () => {
    setAiChatOpen(false);
    setInitialChatMessage('');
  };

  if (loading || !recipe || !currentStep) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  // Handler for applying revised steps from AIChatDialog
  const handleApplyRevisedSteps = async (modifiedRecipe: Recipe) => {
    if (!recipe) {
      return;
    }
    try {
      // Update recipe with all modifications from agent
      await updateRecipe(modifiedRecipe);
      setRecipe(modifiedRecipe);

      // Update current step if still valid
      if (modifiedRecipe.steps && stepIndex < modifiedRecipe.steps.length) {
        setCurrentStep(modifiedRecipe.steps[stepIndex]);
      } else if (modifiedRecipe.steps && modifiedRecipe.steps.length > 0) {
        navigate(`/recipes/${recipeId}/steps/0`);
      }

      setSnackbarMessage('Recipe updated successfully!');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage('Failed to update recipe. Please try again.');
      setSnackbarOpen(true);
    }
  };

  const totalSteps = recipe.steps ? recipe.steps.length : 0;

  // For mobile, show single page view
  if (isMobile) {
    return (
      <>
        <BookContainer>
          <Box sx={{
            width: '100%',
            py: 2,
          }}>
            <Container maxWidth="sm">
              <Box sx={{ mb: 2 }}>
                <IconButton
                  onClick={() => navigate(`/recipes/${recipeId}`)}
                  sx={{
                    color: theme.palette.mode === 'dark' ? 'rgba(255, 248, 240, 0.7)' : 'rgba(139, 69, 19, 0.7)',
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 248, 240, 0.1)' : 'rgba(139, 69, 19, 0.1)',
                    },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              </Box>
              <RecipeStepPage
                step={currentStep}
                stepIndex={stepIndex}
                totalSteps={totalSteps}
                isGeneratingImage={isGeneratingImage}
                isGeneratingAudio={isGeneratingAudio}
                isPlayingAudio={isPlayingAudio}
                onGenerateImage={handleGenerateImage}
                onPlayAudio={handlePlayAudio}
                onStartTimer={handleStartTimer}
                onOpenQuestionDialog={handleOpenQuestionDialog}
                recipe={recipe}
                isMobile={isMobile}
              />
            </Container>
          </Box>
          <BookNavigation currentPageNumber={stepIndex * 2 + 1} />

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={4000}
            onClose={() => setSnackbarOpen(false)}
          >
            <Alert severity="success" onClose={() => setSnackbarOpen(false)}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </BookContainer>

        {/* AI Chat Dialog - Rendered outside BookContainer to avoid z-index issues */}
        <AIChatDialog
          open={aiChatOpen}
          onClose={handleChatClose}
          context={{ recipe, stepIndex }}
          onApplyRevisions={handleApplyRevisedSteps}
          initialMessage={initialChatMessage}
        />

        {/* Proactive Cooking Prompt */}
        {showPrompt && currentStep && (
          <CookingPromptOverlay
            step={currentStep}
            stepIndex={stepIndex}
            onPromptClick={handlePromptClick}
            onDismiss={handlePromptDismiss}
          />
        )}
      </>
    );
  }

  // Desktop two-page book layout
  return (
    <>
    <BookContainer>
      <Box sx={{ 
        width: '100%', 
        py: 4,
      }}>
        {/* Back to recipe button */}
        <Box sx={{ mb: 2, px: 4 }}>
          <IconButton 
            onClick={() => navigate(`/recipes/${recipeId}`)}
              sx={{ 
                color: theme.palette.mode === 'dark' ? 'rgba(255, 248, 240, 0.7)' : 'rgba(139, 69, 19, 0.7)',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 248, 240, 0.1)' : 'rgba(139, 69, 19, 0.1)',
                },
              }}
            >
              <ArrowBackIcon />
              <Typography sx={{ ml: 1, fontFamily: '"Playfair Display", serif' }}>
                Back to Recipe
              </Typography>
          </IconButton>
        </Box>

        {/* Two-page book spread */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          alignItems: 'stretch',
          gap: 0,
          maxWidth: 1200,
          margin: '0 auto',
          minHeight: 'auto',
        }}>
            {/* Left Page - Recipe Sidebar */}
            <Box sx={{ width: '50%' }}>
              <BookPage isLeftPage={true} pageNumber={stepIndex * 2 + 1}>
                <RecipeSidebar
                  recipe={recipe}
                  currentStepIndex={stepIndex}
                  currentScale={currentScale}
                  onStepClick={handleStepClick}
                  onTimerComplete={handleTimerComplete}
                  completedSteps={completedSteps}
                  autoStartTimer={pendingTimer}
                  onRecipeUpdate={handleRecipeUpdate}
                />
              </BookPage>
            </Box>

            {/* Right Page - Step Instructions */}
            <Box sx={{ width: '50%' }}>
              <BookPage isLeftPage={false} pageNumber={stepIndex * 2 + 2}>
                <RecipeStepPage
                  step={currentStep}
                  stepIndex={stepIndex}
                  totalSteps={totalSteps}
                  isGeneratingImage={isGeneratingImage}
                  isGeneratingAudio={isGeneratingAudio}
                  isPlayingAudio={isPlayingAudio}
                  onGenerateImage={handleGenerateImage}
                  onPlayAudio={handlePlayAudio}
                  onStartTimer={handleStartTimer}
                  onOpenQuestionDialog={handleOpenQuestionDialog}
                  isLeftPage={false}
                  recipe={recipe}
                  isMobile={isMobile}
                />
              </BookPage>
            </Box>
          </Box>
      </Box>

      {/* Book Navigation */}
      <BookNavigation currentPageNumber={stepIndex * 2 + 1} />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert severity="success" onClose={() => setSnackbarOpen(false)}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </BookContainer>

    {/* AI Chat Dialog - Rendered outside BookContainer to avoid z-index issues */}
    <AIChatDialog
      open={aiChatOpen}
      onClose={handleChatClose}
      context={{ recipe, stepIndex }}
      onApplyRevisions={handleApplyRevisedSteps}
      initialMessage={initialChatMessage}
    />

    {/* Proactive Cooking Prompt */}
    {showPrompt && currentStep && (
      <CookingPromptOverlay
        step={currentStep}
        stepIndex={stepIndex}
        onPromptClick={handlePromptClick}
        onDismiss={handlePromptDismiss}
      />
    )}
    </>
  );
};

export default RecipeStepScreen;
